import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/userAuthContext";
import {
  LogIn,
  ArrowRight,
  Cpu,
  Server,
  Database,
  Cloud,
  Code,
  Globe,
  Layers,
  Braces,
  PenTool,
  Monitor,
  Lock,
  Printer,
  LayoutDashboard,
  ChevronRight,
} from "lucide-react";

import GoogleIcon from "../assets/google-icon.png";

// Colores del logo de Fronios
const logoColors = {
  blue: "#0088D1", // Azul
  teal: "#33B8B8", // Verde-azulado
  orange: "#F08234", // Naranja
  green: "#6CC24A", // Verde
  darkBlue: "#0F3246", // Azul oscuro del centro
};

const BackgroundIcon = ({ Icon, delay, size = "normal", color }) => {
  const sizeClasses = {
    small: "w-8 h-8",
    normal: "w-12 h-12",
    large: "w-16 h-16",
  };

  return (
    <div
      className="absolute animate-float"
      style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        animationDelay: delay,
        opacity: 0.08 + Math.random() * 0.05,
      }}
    >
      <Icon
        className={`${sizeClasses[size]} transform rotate-${Math.floor(
          Math.random() * 360
        )}`}
        style={{ color }}
      />
    </div>
  );
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showLoginForm, setShowLoginForm] = useState(false);
  const { logIn, googleSignIn, currentLanguage } = useUserAuth();
  const navigate = useNavigate();

  const backgroundIcons = [
    { Icon: Cpu, size: "large", color: logoColors.blue },
    { Icon: Server, size: "normal", color: logoColors.teal },
    { Icon: Database, size: "large", color: logoColors.orange },
    { Icon: Cloud, size: "normal", color: logoColors.green },
    { Icon: Code, size: "large", color: logoColors.blue },
    { Icon: Globe, size: "small", color: logoColors.teal },
    { Icon: Layers, size: "normal", color: logoColors.orange },
    { Icon: Braces, size: "large", color: logoColors.green },
    { Icon: PenTool, size: "normal", color: logoColors.blue },
    { Icon: Monitor, size: "small", color: logoColors.teal },
    { Icon: Lock, size: "normal", color: logoColors.orange },
    { Icon: Printer, size: "normal", color: logoColors.green },
    { Icon: LayoutDashboard, size: "large", color: logoColors.blue },
  ];

  // Traducciones para los textos de la página
  const translations = {
    en: {
      title: "The leading platform for enterprise technology solutions",
      login: "Sign In",
      email: "Email",
      emailPlaceholder: "your@email.com",
      password: "Password",
      passwordPlaceholder: "••••••••",
      continueWith: "Or continue with",
      googleLogin: "Sign In with Google",
      noAccount: "Don't have an account?",
      createOne: "Create one!",
      copyright: "© 2025 Fronios. All rights reserved.",
      errorTitle: "Error",
    },
    es: {
      title: "La plataforma líder en soluciones tecnológicas empresariales",
      login: "Iniciar Sesión",
      email: "Correo Electrónico",
      emailPlaceholder: "tu@email.com",
      password: "Contraseña",
      passwordPlaceholder: "••••••••",
      continueWith: "O continúa con",
      googleLogin: "Iniciar Sesión con Google",
      noAccount: "¿No tienes una cuenta?",
      createOne: "¡Crea una!",
      copyright: "© 2025 Fronios. Todos los derechos reservados.",
      errorTitle: "Error",
    },
  };

  // Seleccionar el idioma actual
  const t = translations[currentLanguage || "es"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await logIn(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGoogleSignIn = async (e) => {
    e.preventDefault();
    try {
      await googleSignIn();
      navigate("/dashboard");
    } catch (error) {
      console.log(error.message);
      setError(
        currentLanguage === "en"
          ? "Error signing in with Google"
          : "Error al iniciar sesión con Google"
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute w-full h-full pointer-events-none">
        {backgroundIcons.map((item, i) => (
          <BackgroundIcon
            key={i}
            Icon={item.Icon}
            delay={`${i * 0.2}s`}
            size={item.size}
            color={item.color}
          />
        ))}
      </div>

      {/* Subtle Grid Background */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `linear-gradient(${logoColors.darkBlue}15 1px, transparent 1px), linear-gradient(90deg, ${logoColors.darkBlue}15 1px, transparent 1px)`,
          backgroundSize: "30px 30px",
        }}
      ></div>

      <div className="w-full max-w-md z-10">
        {/* Logo */}
        <div className="text-center mb-8 transform hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-center mb-3">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/fronios-48861.firebasestorage.app/o/logos%2FScreenshot_2025-04-07_at_12.48.37_p.m.-removebg-preview.png?alt=media&token=2603b714-7c47-4d62-8f94-9efd5d7cbed0"
              alt="Fronios Logo"
              className="h-12 w-auto"
            />
            <span className="font-sans tracking-tight text-gray-800 ml-2">
              <span className="text-2xl font-light">fronios</span>
            </span>
          </div>
          <p className="text-gray-600">{t.title}</p>
        </div>

        {/* Login Container */}
        <div className="bg-white rounded-lg shadow-xl p-8 transition-all duration-300 hover:shadow-2xl border border-gray-100">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-6 rounded-lg">
              <p className="font-medium">{t.errorTitle}</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Initial Login Button */}
          {!showLoginForm && (
            <button
              onClick={() => setShowLoginForm(true)}
              className="w-full py-3 bg-blue-500 text-white rounded-lg text-lg font-medium hover:bg-blue-600 transition-all transform hover:scale-[1.02] shadow-lg mb-6 flex items-center justify-center group"
              style={{ backgroundColor: logoColors.blue }}
            >
              {t.login}
              <LogIn className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          )}

          {/* Login Form */}
          {showLoginForm && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {t.email}
                </label>
                <input
                  type="email"
                  placeholder={t.emailPlaceholder}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder-gray-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {t.password}
                </label>
                <input
                  type="password"
                  placeholder={t.passwordPlaceholder}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all placeholder-gray-400"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 text-white rounded-lg text-lg font-medium transition-all transform hover:scale-[1.02] shadow-lg flex items-center justify-center group"
                style={{ backgroundColor: logoColors.blue }}
              >
                {t.login}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                {t.continueWith}
              </span>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            className="w-full py-3 px-6 bg-white border-2 rounded-lg text-lg font-medium hover:bg-gray-50 transition-all transform hover:scale-[1.02] shadow-md flex items-center justify-center space-x-3 group"
            style={{ borderColor: logoColors.blue, color: logoColors.blue }}
          >
            <img
              src={GoogleIcon}
              alt="Google"
              className="w-6 h-6 group-hover:scale-110 transition-transform"
            />
            <span>{t.googleLogin}</span>
          </button>

          {/* Sign Up Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              {t.noAccount}{" "}
              <Link
                to="/signup"
                className="font-semibold hover:underline transition-colors"
                style={{ color: logoColors.blue }}
              >
                {t.createOne}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>{t.copyright}</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg) scale(1);
          }
          50% {
            transform: translateY(-20px) rotate(10deg) scale(1.1);
          }
        }
        .animate-float {
          animation: float 15s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Login;

